// Text of the Copilot settings screens (Arabic / English). The inbox text stays in
// ../copilotAdminText.js; category names come from the backend.

export const SETTINGS_TEXT = {
  en: {
    open: "Settings",
    title: "Copilot settings",
    subtitle: "How LUD Copilot greets customers, the replies your team reuses, and your notifications.",
    backToInbox: "Back to the inbox",
    tabsLabel: "Copilot settings sections",
    tabs: { general: "General", auto: "Automatic replies", replies: "Saved replies", faqs: "FAQs", notifications: "Notifications" },
    loading: "Loading…",
    retry: "Try again",
    loadError: "Could not load the settings.",
    viewOnly: "You can view these settings. Only managers can change them.",
    saved: "Saved",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    close: "Close",
    on: "On",
    off: "Off",
    arabic: "Arabic",
    english: "English",
    general: {
      supportTitle: "Copilot support",
      supportBody: "Customers contact LUD from the chat button on every page, signed in or as guests. Staff answer from the Copilot inbox.",
      autoStatus: "Automatic first reply",
      autoOn: "On: every new conversation receives a welcome message.",
      autoOff: "Off: new conversations are not greeted automatically.",
      manageAuto: "Edit automatic replies",
      categoriesTitle: "Conversation categories",
      categoriesBody: "The customer picks one of these when starting a conversation. The automatic reply and the saved replies follow it.",
      contentTitle: "Three different kinds of text",
      contentBody: "Customers and agents see different content. Each has its own place in these settings.",
      content: [
        ["auto", "Automatic welcome reply", "Sent by the system as the first message of a new conversation."],
        ["replies", "Saved agent replies", "Ready-made answers your team inserts into a reply. Customers never browse them."],
        ["faqs", "Frequently asked questions", "Answers customers browse in the chat before (or instead of) contacting support. No conversation is created."],
      ],
      behaviorTitle: "How conversations behave",
      behavior: [
        ["Open", "Waiting for support to answer."],
        ["Pending", "Waiting for the customer. A support reply moves a conversation here."],
        ["Resolved", "Closed. If the customer writes again it reopens by itself."],
        ["First reply", "The first support reply to an unassigned conversation assigns it to that agent."],
        ["Guest access", "Guests return with a private key kept in their browser (14 days after the last message) or with the single-use link in their emails."],
        ["Attachments", "JPEG, PNG, WebP or PDF, up to 5 MB each, 5 per message."],
      ],
      fixedNote: "These rules are built into Copilot and are not editable.",
    },
    auto: {
      title: "Automatic first reply",
      description: "Sent once, as LUD Support, when a customer starts a conversation. It does not assign the conversation, change its status, count as a reply or alert your team.",
      enabled: "Send an automatic reply to new conversations",
      language: "Language",
      languageHint: "Customers receive the language they were using when they started the conversation.",
      sender: "Shown as",
      default: "Default",
      customized: "Customized",
      edit: "Edit",
      reset: "Reset to default",
      confirmReset: "Use the default text again?",
      preview: "How the customer sees it",
      editorLabel: "Message text",
      placeholders: "Insert",
      placeholderHelp: "{{customer_name}} is replaced by the customer's name and {{reference}} by the conversation reference.",
      sampleName: "Sample Customer",
      empty: "The text cannot be empty. Use \"Reset to default\" to go back to the default.",
      tooLong: (n) => `At most ${n} characters`,
      count: (n, max) => `${n} / ${max}`,
      savedTemplate: "Template saved. It applies to new conversations only.",
      switchedOn: "Automatic replies are on.",
      switchedOff: "Automatic replies are off.",
      resetDone: "Back to the default text.",
      appliesToNew: "Changes apply to new conversations only; messages already sent stay as they were.",
    },
    replies: {
      title: "Saved replies",
      description: "Ready-made answers your team can insert into a reply. System replies come with Copilot; custom replies are yours.",
      search: "Search saved replies",
      category: "Category",
      allCategories: "All categories",
      language: "Language",
      allLanguages: "All languages",
      source: "Source",
      allSources: "All",
      system: "System",
      custom: "Custom",
      customized: "Customized",
      anyLang: "Any language",
      anyCategory: "All categories",
      newReply: "New reply",
      edit: "Edit",
      archive: "Delete",
      confirmArchiveTitle: "Delete this saved reply?",
      confirmArchive: (title) => `"${title}" will no longer appear for your team.`,
      empty: "No saved replies yet.",
      emptyManager: "No saved replies yet. Create one, or install the ready-made set with the seed command (see the Copilot operations notes).",
      noMatch: "No saved reply matches these filters.",
      clearFilters: "Clear filters",
      used: (n) => `Used ${n} times`,
      editorTitleNew: "New saved reply",
      editorTitleEdit: "Edit saved reply",
      fieldTitle: "Title",
      fieldBody: "Reply text",
      fieldLang: "Language",
      fieldCategory: "Category",
      placeholderHelp: "Placeholders: {{customer_name}}, {{reference}} and {{agent_name}} are filled in when the reply is inserted.",
      previewTitle: "Preview",
      sampleAgent: "Your name",
      titleRequired: "Enter a title (at least 2 characters)",
      bodyRequired: "Enter the reply text",
      bodyLong: "The reply must be at most 5000 characters",
      created: "Saved reply created.",
      updated: "Saved reply updated.",
      archived: "Saved reply deleted.",
      seedEdited: "A system reply you change becomes yours: updates of the ready-made set will not overwrite it.",
    },
    notifications: {
      title: "Your notifications",
      description: "Choose how you hear about Copilot conversations. These settings are yours and apply to your account only.",
      email: "Email notifications",
      emailHint: "Replies, resolutions, assignments and customer messages are also sent to your email. Emails are notifications only: to answer, use the chat or the inbox.",
      inApp: "In-app notifications",
      inAppHint: "Alerts on the Notifications page, with a badge in the menu.",
      required: "A confirmation for each conversation you start is always sent, whatever you choose here.",
      openPage: "Open the Notifications page",
      loadError: "Could not load your notification settings.",
    },
    faqs: {
      title: "Frequently asked questions",
      description: "The questions customers browse in the chat before contacting support. They are separate from the automatic welcome reply and from the saved replies your team sends.",
      search: "Search questions and answers",
      category: "Topic",
      allCategories: "All topics",
      visibility: "Who sees it",
      allVisibility: "Everyone",
      publicLabel: "Everyone (public)",
      authenticatedLabel: "Signed-in users only",
      status: "Status",
      allStatus: "Any status",
      active: "Active",
      inactive: "Inactive",
      source: "Source",
      allSources: "All",
      system: "System",
      custom: "Custom",
      customized: "Customized",
      newFaq: "New FAQ",
      edit: "Edit",
      preview: "Preview",
      remove: "Delete",
      moveUp: "Move up",
      moveDown: "Move down",
      activeSwitch: "Show this FAQ to customers",
      noMatch: "No FAQ matches these filters.",
      empty: "No FAQs yet.",
      emptyManager: "No FAQs yet. Create one, or install the ready-made set with the seed command (see the Copilot operations notes).",
      clearFilters: "Clear filters",
      orderNote: "Order can be changed when only a topic filter is applied.",
      stats: (helpful, notHelpful, escalated) => `Helpful ${helpful} · Not helpful ${notHelpful} · Went on to contact support ${escalated}`,
      statsNone: "No feedback yet",
      confirmDeleteTitle: "Delete this FAQ?",
      confirmDelete: (title) => `"${title}" and its feedback counts will be removed. System FAQs cannot be deleted; deactivate them instead.`,
      systemNoDelete: "System FAQs cannot be deleted. Deactivate it to hide it.",
      created: "FAQ created.",
      updated: "FAQ updated.",
      deleted: "FAQ deleted.",
      activated: "The FAQ is now shown to customers.",
      deactivated: "The FAQ is hidden from customers.",
      reordered: "Order saved.",
      editorTitleNew: "New FAQ",
      editorTitleEdit: "Edit FAQ",
      fieldCategory: "Topic",
      fieldVisibility: "Who sees it",
      fieldQuestionAr: "Question (Arabic)",
      fieldQuestionEn: "Question (English)",
      fieldAnswerAr: "Answer (Arabic)",
      fieldAnswerEn: "Answer (English)",
      fieldActive: "Show to customers",
      authOnlyTopic: "This topic is for signed-in users only, so its FAQs are never shown to visitors.",
      noPersonal: "Write general answers only: never include customer-specific data (names, invoice numbers, amounts).",
      seedEdited: "A system FAQ you change becomes yours: updates of the ready-made set will not overwrite it.",
      questionRequired: "Enter the question in both languages.",
      answerRequired: "Enter the answer in both languages.",
      questionLong: "A question must be at most 200 characters.",
      answerLong: "An answer must be at most 4000 characters.",
      previewTitle: "How customers see it",
      previewLang: "Preview language",
      previewVisibility: (visible) => (visible === "authenticated" ? "Shown to signed-in users only." : "Shown to everyone, including visitors who are not signed in."),
      previewInactive: "This FAQ is inactive: customers do not see it.",
      previewAsk: "Did this answer your question?",
      previewYes: "Yes, that helped",
      previewNo: "No, I need help",
      close: "Close",
    },
    errors: { offline: "No connection. Check your internet and try again.", forbidden: "You do not have permission to do that.", rate: "Too many requests. Try again in a moment.", server: "Something went wrong. Please try again." },
  },
  ar: {
    open: "الإعدادات",
    title: "إعدادات المساعد",
    subtitle: "كيف يستقبل مساعد لُد العملاء، والردود التي يعيد فريقك استخدامها، وإشعاراتك.",
    backToInbox: "العودة إلى الصندوق",
    tabsLabel: "أقسام إعدادات المساعد",
    tabs: { general: "عام", auto: "الردود التلقائية", replies: "الردود الجاهزة", faqs: "الأسئلة الشائعة", notifications: "الإشعارات" },
    loading: "جارٍ التحميل…",
    retry: "إعادة المحاولة",
    loadError: "تعذّر تحميل الإعدادات.",
    viewOnly: "يمكنك عرض هذه الإعدادات. التعديل متاح للمديرين فقط.",
    saved: "تم الحفظ",
    cancel: "إلغاء",
    save: "حفظ",
    saving: "جارٍ الحفظ…",
    close: "إغلاق",
    on: "مفعّل",
    off: "متوقف",
    arabic: "العربية",
    english: "English",
    general: {
      supportTitle: "دعم المساعد",
      supportBody: "يتواصل العملاء مع لُد من زر المحادثة في كل صفحة، سواء كانوا مسجّلين أو زوّارًا. ويرد الفريق من صندوق المساعد.",
      autoStatus: "الرد التلقائي الأول",
      autoOn: "مفعّل: تصل كل محادثة جديدة رسالة ترحيب.",
      autoOff: "متوقف: لا تُرسل رسالة ترحيب تلقائيًا للمحادثات الجديدة.",
      manageAuto: "تعديل الردود التلقائية",
      categoriesTitle: "تصنيفات المحادثات",
      categoriesBody: "يختار العميل أحد هذه التصنيفات عند بدء المحادثة، ويتبعه الرد التلقائي والردود الجاهزة.",
      contentTitle: "ثلاثة أنواع مختلفة من النصوص",
      contentBody: "يرى العملاء والوكلاء محتوى مختلفًا، ولكل نوع مكانه في هذه الإعدادات.",
      content: [
        ["auto", "رسالة الترحيب التلقائية", "يرسلها النظام كأول رسالة في المحادثة الجديدة."],
        ["replies", "ردود الوكلاء الجاهزة", "إجابات جاهزة يدرجها فريقك في الرد. لا يتصفحها العملاء."],
        ["faqs", "الأسئلة الشائعة", "إجابات يتصفحها العملاء في المحادثة قبل التواصل مع الدعم أو بدلًا منه، ولا تنشئ أي محادثة."],
      ],
      behaviorTitle: "كيف تعمل المحادثات",
      behavior: [
        ["مفتوحة", "بانتظار رد الدعم."],
        ["معلّقة", "بانتظار العميل. يؤدي رد الدعم إلى نقل المحادثة إلى هذه الحالة."],
        ["محلولة", "مغلقة. إذا كتب العميل مرة أخرى تُعاد فتحها تلقائيًا."],
        ["أول رد", "أول رد من الدعم على محادثة غير مسندة يُسندها إلى ذلك الوكيل."],
        ["وصول الزائر", "يعود الزائر بمفتاح خاص محفوظ في متصفحه (14 يومًا بعد آخر رسالة) أو بالرابط ذي الاستخدام الواحد في رسائله."],
        ["المرفقات", "صور JPEG أو PNG أو WebP أو ملف PDF، حتى 5 ميجابايت للملف و5 ملفات للرسالة."],
      ],
      fixedNote: "هذه القواعد مدمجة في المساعد وغير قابلة للتعديل.",
    },
    auto: {
      title: "الرد التلقائي الأول",
      description: "يُرسل مرة واحدة باسم دعم لُد عند بدء العميل محادثة. لا يُسند المحادثة ولا يغيّر حالتها ولا يُحتسب ردًّا ولا ينبّه فريقك.",
      enabled: "إرسال رد تلقائي للمحادثات الجديدة",
      language: "اللغة",
      languageHint: "يتلقى العميل اللغة التي كان يستخدمها عند بدء المحادثة.",
      sender: "يظهر باسم",
      default: "افتراضي",
      customized: "مخصّص",
      edit: "تعديل",
      reset: "استعادة الافتراضي",
      confirmReset: "العودة إلى النص الافتراضي؟",
      preview: "كما يراه العميل",
      editorLabel: "نص الرسالة",
      placeholders: "إدراج",
      placeholderHelp: "يُستبدل {{customer_name}} باسم العميل و{{reference}} برقم المحادثة.",
      sampleName: "عميل تجريبي",
      empty: "لا يمكن أن يكون النص فارغًا. استخدم «استعادة الافتراضي» للعودة إلى النص الافتراضي.",
      tooLong: (n) => `${n} حرفًا كحد أقصى`,
      count: (n, max) => `${n} / ${max}`,
      savedTemplate: "تم حفظ القالب. يسري على المحادثات الجديدة فقط.",
      switchedOn: "الردود التلقائية مفعّلة.",
      switchedOff: "الردود التلقائية متوقفة.",
      resetDone: "تمت العودة إلى النص الافتراضي.",
      appliesToNew: "تسري التغييرات على المحادثات الجديدة فقط؛ الرسائل المرسلة سابقًا تبقى كما هي.",
    },
    replies: {
      title: "الردود الجاهزة",
      description: "إجابات جاهزة يدرجها فريقك في الرد. ردود النظام تأتي مع المساعد، والردود المخصّصة من إنشائك.",
      search: "ابحث في الردود الجاهزة",
      category: "التصنيف",
      allCategories: "كل التصنيفات",
      language: "اللغة",
      allLanguages: "كل اللغات",
      source: "المصدر",
      allSources: "الكل",
      system: "النظام",
      custom: "مخصّص",
      customized: "معدَّل",
      anyLang: "أي لغة",
      anyCategory: "كل التصنيفات",
      newReply: "رد جديد",
      edit: "تعديل",
      archive: "حذف",
      confirmArchiveTitle: "حذف هذا الرد الجاهز؟",
      confirmArchive: (title) => `لن يظهر «${title}» لفريقك بعد الآن.`,
      empty: "لا توجد ردود جاهزة بعد.",
      emptyManager: "لا توجد ردود جاهزة بعد. أنشئ ردًّا، أو ثبّت المجموعة الجاهزة بأمر التهيئة (راجع ملاحظات تشغيل المساعد).",
      noMatch: "لا يوجد رد جاهز يطابق هذه التصفية.",
      clearFilters: "مسح التصفية",
      used: (n) => `استُخدم ${n} مرة`,
      editorTitleNew: "رد جاهز جديد",
      editorTitleEdit: "تعديل الرد الجاهز",
      fieldTitle: "العنوان",
      fieldBody: "نص الرد",
      fieldLang: "اللغة",
      fieldCategory: "التصنيف",
      placeholderHelp: "العناصر البديلة: {{customer_name}} و{{reference}} و{{agent_name}} تُملأ عند إدراج الرد.",
      previewTitle: "معاينة",
      sampleAgent: "اسمك",
      titleRequired: "أدخل عنوانًا (حرفان على الأقل)",
      bodyRequired: "أدخل نص الرد",
      bodyLong: "يجب ألا يزيد الرد عن 5000 حرف",
      created: "تم إنشاء الرد الجاهز.",
      updated: "تم تحديث الرد الجاهز.",
      archived: "تم حذف الرد الجاهز.",
      seedEdited: "رد النظام الذي تعدّله يصبح ملكك: لن تستبدله تحديثات المجموعة الجاهزة.",
    },
    notifications: {
      title: "إشعاراتك",
      description: "اختر كيف تصلك أخبار محادثات المساعد. هذه الإعدادات خاصة بك وتخص حسابك فقط.",
      email: "إشعارات البريد الإلكتروني",
      emailHint: "تُرسل الردود وإشعارات الحل والإسناد ورسائل العملاء إلى بريدك أيضًا. رسائل البريد إشعارات فقط: للرد استخدم المحادثة أو الصندوق.",
      inApp: "الإشعارات داخل التطبيق",
      inAppHint: "تنبيهات في صفحة التنبيهات مع شارة في القائمة.",
      required: "يُرسل دائمًا تأكيد لكل محادثة تبدؤها، مهما كان اختيارك هنا.",
      openPage: "فتح صفحة التنبيهات",
      loadError: "تعذّر تحميل إعدادات الإشعارات.",
    },
    faqs: {
      title: "الأسئلة الشائعة",
      description: "الأسئلة التي يتصفحها العملاء في المحادثة قبل التواصل مع الدعم. وهي منفصلة عن رسالة الترحيب التلقائية وعن الردود الجاهزة التي يرسلها فريقك.",
      search: "ابحث في الأسئلة والإجابات",
      category: "الموضوع",
      allCategories: "كل المواضيع",
      visibility: "من يراه",
      allVisibility: "الجميع",
      publicLabel: "الجميع (عام)",
      authenticatedLabel: "المستخدمون المسجّلون فقط",
      status: "الحالة",
      allStatus: "أي حالة",
      active: "مفعّل",
      inactive: "غير مفعّل",
      source: "المصدر",
      allSources: "الكل",
      system: "النظام",
      custom: "مخصّص",
      customized: "معدَّل",
      newFaq: "سؤال جديد",
      edit: "تعديل",
      preview: "معاينة",
      remove: "حذف",
      moveUp: "تحريك لأعلى",
      moveDown: "تحريك لأسفل",
      activeSwitch: "إظهار هذا السؤال للعملاء",
      noMatch: "لا يوجد سؤال يطابق هذه التصفية.",
      empty: "لا توجد أسئلة شائعة بعد.",
      emptyManager: "لا توجد أسئلة شائعة بعد. أنشئ سؤالًا، أو ثبّت المجموعة الجاهزة بأمر التهيئة (راجع ملاحظات تشغيل المساعد).",
      clearFilters: "مسح التصفية",
      orderNote: "يمكن تغيير الترتيب عند تطبيق تصفية الموضوع فقط.",
      stats: (helpful, notHelpful, escalated) => `مفيد ${helpful} · غير مفيد ${notHelpful} · تواصل مع الدعم بعدها ${escalated}`,
      statsNone: "لا توجد تقييمات بعد",
      confirmDeleteTitle: "حذف هذا السؤال؟",
      confirmDelete: (title) => `سيُحذف «${title}» مع عدادات تقييمه. لا يمكن حذف أسئلة النظام؛ يمكن إيقافها بدلًا من ذلك.`,
      systemNoDelete: "لا يمكن حذف أسئلة النظام. أوقفها لإخفائها.",
      created: "تم إنشاء السؤال.",
      updated: "تم تحديث السؤال.",
      deleted: "تم حذف السؤال.",
      activated: "أصبح السؤال ظاهرًا للعملاء.",
      deactivated: "تم إخفاء السؤال عن العملاء.",
      reordered: "تم حفظ الترتيب.",
      editorTitleNew: "سؤال جديد",
      editorTitleEdit: "تعديل السؤال",
      fieldCategory: "الموضوع",
      fieldVisibility: "من يراه",
      fieldQuestionAr: "السؤال (بالعربية)",
      fieldQuestionEn: "السؤال (بالإنجليزية)",
      fieldAnswerAr: "الإجابة (بالعربية)",
      fieldAnswerEn: "الإجابة (بالإنجليزية)",
      fieldActive: "إظهاره للعملاء",
      authOnlyTopic: "هذا الموضوع للمستخدمين المسجّلين فقط، لذلك لا تظهر أسئلته للزوّار أبدًا.",
      noPersonal: "اكتب إجابات عامة فقط: لا تضع بيانات خاصة بعميل (أسماء أو أرقام فواتير أو مبالغ).",
      seedEdited: "سؤال النظام الذي تعدّله يصبح ملكك: لن تستبدله تحديثات المجموعة الجاهزة.",
      questionRequired: "أدخل السؤال باللغتين.",
      answerRequired: "أدخل الإجابة باللغتين.",
      questionLong: "يجب ألا يزيد السؤال عن 200 حرف.",
      answerLong: "يجب ألا تزيد الإجابة عن 4000 حرف.",
      previewTitle: "كما يراه العميل",
      previewLang: "لغة المعاينة",
      previewVisibility: (visible) => (visible === "authenticated" ? "يظهر للمستخدمين المسجّلين فقط." : "يظهر للجميع، بمن فيهم الزوّار غير المسجّلين."),
      previewInactive: "هذا السؤال غير مفعّل: لا يراه العملاء.",
      previewAsk: "هل أجابت هذه المعلومات عن استفسارك؟",
      previewYes: "نعم، شكرًا",
      previewNo: "لا، أحتاج مساعدة",
      close: "إغلاق",
    },
    errors: { offline: "لا يوجد اتصال. تحقق من الإنترنت وحاول مرة أخرى.", forbidden: "ليست لديك صلاحية لتنفيذ ذلك.", rate: "طلبات كثيرة. حاول بعد قليل.", server: "حدث خطأ ما. حاول مرة أخرى." },
  },
};

export function settingsError(error, t) {
  if (error?.network) return t.errors.offline;
  if (error?.status === 403) return t.errors.forbidden;
  if (error?.status === 429) return t.errors.rate;
  if (error?.status === 400 && error.message) return error.message;
  return t.errors.server;
}

// Same rules as the backend (copilotSettingsService.fillPlaceholders): a missing value and any unknown
// placeholder disappear with the punctuation they leave behind, "undefined" is never printed.
export function fillTemplate(text, values = {}) {
  return String(text)
    .replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_, key) => String(values[key.toLowerCase()] || "").trim())
    .replace(/\(\s*\)/g, "")
    .replace(/[ \t]+([،,.!؟?:;])/g, "$1")
    .replace(/([،,]\s*)(?=[\n]|$)/g, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}
